<template>
  <div class="public-shop">
    <header v-if="shop" class="shop-header" :style="{ background: themeGradient }">
      <div class="container py-3">
        <div class="d-flex justify-content-between align-items-center">
          <router-link :to="shopPath('')" class="header-link">
            <i class="fas fa-arrow-left"></i> {{ shop.name }}
          </router-link>
          <router-link :to="shopPath('/keranjang')" class="btn btn-light btn-sm">
            <i class="fas fa-shopping-cart"></i> Cart ({{ cartCount }})
          </router-link>
        </div>
      </div>
    </header>

    <div class="container mt-4 pb-5">
      <div v-if="loading" class="text-center p-5">
        <div class="spinner-border text-primary"></div>
        <p class="text-muted mt-3">Memuat produk...</p>
      </div>

      <div v-else-if="!product" class="empty-state">
        <i class="fas fa-box-open empty-state-icon"></i>
        <h4>Produk tidak ditemukan</h4>
        <p class="text-muted">Produk mungkin sudah tidak tersedia.</p>
        <router-link :to="shopPath('')" class="btn btn-primary mt-2">Kembali ke Toko</router-link>
      </div>

      <div v-else class="row">
        <div class="col-md-6 mb-4">
          <div class="shop-card overflow-hidden">
            <img :src="productImage(activeImage || product.image_url, product.type)" class="main-image w-100" :alt="product.name">
            <div v-if="galleryImages.length > 1" class="gallery-thumbs p-3">
              <img v-for="(img, idx) in galleryImages" :key="idx" :src="img"
                class="thumb rounded border" :class="{ 'border-primary': img === activeImage }"
                @click="activeImage = img">
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <h2 class="product-title">{{ product.name }}</h2>
          <h3 class="text-primary mb-3">{{ formatRupiah(product.price) }}</h3>
          <div class="mb-3">
            <span v-if="product.type === 'digital'" class="badge badge-info">Produk Digital</span>
            <span v-if="product.stock !== null && product.type === 'physical'" class="badge badge-secondary ml-1">
              Stok: {{ product.stock }}
            </span>
            <span v-if="product.stock !== null && product.stock <= 5 && product.type === 'physical'" class="badge badge-warning ml-1">
              Hampir habis!
            </span>
          </div>

          <div v-if="product.description" class="shop-card p-3 mb-3 description-box" v-html="formatDescription(product.description)"></div>

          <div v-if="variantList.length > 0" class="shop-card p-3 mb-3">
            <div v-for="(variant, idx) in variantList" :key="idx" class="mb-3">
              <label class="font-weight-bold mb-2">{{ variant.name }}</label>
              <div class="d-flex flex-wrap" style="gap:8px;">
                <button v-for="opt in variant.options" :key="opt"
                  class="btn btn-sm"
                  :class="selectedVariants[variant.name] === opt ? 'btn-primary' : 'btn-outline-primary'"
                  @click="selectVariant(variant.name, opt)">
                  {{ opt }}
                </button>
              </div>
            </div>
          </div>

          <div class="shop-card p-4">
            <label class="font-weight-bold">Jumlah</label>
            <input v-model.number="qty" type="number" class="form-control mb-3 qty-field" min="1" :max="product.stock || 999">
            <button class="btn btn-primary btn-lg btn-block" @click="addToCart">
              <i class="fas fa-cart-plus"></i> Tambah ke Keranjang
            </button>
            <button class="btn btn-success btn-lg btn-block mt-2" @click="buyNow">
              <i class="fas fa-bolt"></i> Beli Sekarang
            </button>
            <a v-if="shop && shop.owner_phone" :href="waLink" target="_blank" class="btn btn-outline-success btn-block mt-2">
              <i class="fab fa-whatsapp"></i> Chat WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import publicShopPaths from './public_shop_paths';
import { applyShopTheme, shopHeaderBackground, productImage, shopPublicMixin } from '@/components/shop_public/shop_public_utils';
import { trackViewContent, trackAddToCart } from '@/components/shop_public/shop_tracking';
import '@/components/shop_public/shop_public.css';

export default {
  mixins: [publicShopPaths, shopPublicMixin],
  data() {
    return { productSlug: '', shop: null, product: null, loading: true, qty: 1, selectedVariants: {}, activeImage: '' };
  },
  computed: {
    cartCount() { return this.$store.getters['public_shop/cartCount'] || 0; },
    themeGradient() {
      return shopHeaderBackground(this.shop);
    },
    galleryImages() {
      if (!this.product) return [];
      const imgs = [];
      if (this.product.image_url) imgs.push(this.product.image_url);
      if (this.product.images) {
        try {
          const extra = typeof this.product.images === 'string' ? JSON.parse(this.product.images) : this.product.images;
          if (Array.isArray(extra)) {
            extra.forEach(u => { if (u && !imgs.includes(u)) imgs.push(u); });
          }
        } catch (e) { /* noop */ }
      }
      return imgs.length ? imgs : [productImage(null, this.product.type)];
    },
    variantList() {
      if (!this.product?.variants) return [];
      try {
        const v = typeof this.product.variants === 'string' ? JSON.parse(this.product.variants) : this.product.variants;
        return v || [];
      } catch (e) { return []; }
    },
    waLink() {
      if (!this.shop) return '#';
      const n = (this.shop.owner_phone || '').replace(/^0/, '62').replace(/[^\d]/g, '');
      const text = encodeURIComponent('Halo, saya tertarik dengan produk ' + this.product.name + ' di toko ' + this.shop.name);
      return `https://wa.me/${n}?text=${text}`;
    },
  },
  async mounted() {
    this.productSlug = this.$route.params.productSlug;
    this.$store.dispatch('public_shop/cart_load', this.effectiveSlug);
    await this.load();
  },
  methods: {
    productImage,
    async load() {
      this.loading = true;
      try {
        if (!this.effectiveSlug && this.onCustomDomain) {
          await this.$store.dispatch('public_shop/public_get_shop_by_domain');
        }
        const useSlug = this.effectiveSlug;
        await this.$store.dispatch('public_shop/public_get_shop', useSlug);
        this.shop = this.$store.state.public_shop.shop;
        applyShopTheme(this.shop);

        const r = await this.$store.dispatch('public_shop/public_get_products', { slug: useSlug, params: {} });
        const found = (r.data.data.products || []).find(p => p.slug === this.productSlug);
        if (found) {
          const r2 = await this.$store.dispatch('public_shop/public_get_product', found.id);
          this.product = r2.data.data.product;
          this.activeImage = this.galleryImages[0] || '';
          trackViewContent({
            product_id: this.product.id,
            product_name: this.product.name,
            price: this.product.price,
            type: this.product.type,
          });
        } else {
          this.product = null;
        }
      } catch (e) { this.product = null; }
      this.loading = false;
    },
    selectVariant(name, opt) {
      this.$set(this.selectedVariants, name, opt);
    },
    addToCart() {
      if (this.qty < 1) return;
      this.$store.dispatch('public_shop/cart_add', {
        slug: this.effectiveSlug,
        product: {
          product_id: this.product.id,
          product_name: this.product.name,
          price: this.product.price,
          image_url: this.product.image_url,
          type: this.product.type,
          weight_grams: this.product.weight_grams || 0,
          quantity: this.qty,
          selected_variants: { ...this.selectedVariants },
        },
      });
      trackAddToCart({
        product_id: this.product.id,
        product_name: this.product.name,
        price: this.product.price,
        type: this.product.type,
      }, this.qty);
      this.$toast.success('Ditambahkan ke keranjang');
    },
    buyNow() {
      this.addToCart();
      this.$router.push(this.shopPath('/checkout'));
    },
    formatDescription(d) { return (d || '').replace(/\n/g, '<br>'); },
  },
};
</script>

<style scoped>
.main-image {
  max-height: 420px;
  object-fit: contain;
  background: #fff;
  padding: 12px;
}
.gallery-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid #eee;
}
.thumb {
  width: 72px;
  height: 72px;
  object-fit: cover;
  cursor: pointer;
}
.product-title {
  font-weight: 600;
  line-height: 1.3;
}
.qty-field {
  max-width: 120px;
}
.description-box {
  line-height: 1.6;
  color: #555;
}
</style>
